/**
 * Domínio: UI de Atendimento (tickets).
 *
 * Agrupa eventos de pipeline, slots de render e contextos tipados da área de
 * atendimento. Acesse via `tickets.events.ACCEPT`, `tickets.slots.LIST_ITEM_ACTIONS`.
 *
 * Os valores (string literais) são o contrato com o frontend e com os plugins —
 * não altere as strings, só adicione novas.
 *
 * Os slots vivem num REGISTRO só (`ticketsSlots`): nome, descrição e contrato
 * de dados juntos. `tickets.slots`, `tickets.slotContext` e os tipos
 * `*Context` são derivados dele — ver `ui/slotSchema.ts`.
 */
import type { InterceptContextBase } from "@/ui/types";
import {
  defineSlotRegistry,
  legacySlotContextOf,
  slotNamesOf,
  t,
  type InferShape,
  type SlotContextMapOf,
} from "@/ui/slotSchema";

// ─── contratos reutilizados por mais de um slot ─────────────────────────────

/** Ticket aberto na tela — o que todo slot da conversa recebe. */
const ticketOpen = {
  ticketId: t.number,
  contactId: t.number,
  channel: t.note(t.string, 'ex: "whatsapp", "notificamehub"'),
  whatsappId: t.number,
} as const;

/** Uma mensagem da conversa (menu, conteúdo, rodapé, reações). */
const messageBase = {
  messageId: t.string,
  ticketId: t.number,
  body: t.string,
  fromMe: t.boolean,
  mediaUrl: t.optional(t.string),
} as const;

/** Mensagem + de quem ela é (canal/conexão) — para agir só nas SUAS. */
const messageOwned = {
  ...messageBase,
  createdAt: t.note(t.string, "ISO"),
  channel: t.optional(t.note(t.string, "filtre por aqui antes de esconder o nativo")),
  whatsappId: t.optional(t.number),
  wid: t.optional(t.note(t.string, "id externo no canal")),
  isGroup: t.optional(t.boolean),
  /**
   * Espaço de cada plugin nesta mensagem (`pluginId → chave → valor`), como o
   * backend o gravou. O app passa adiante e NÃO lê dentro — é aqui que o
   * plugin encontra o que ele mesmo guardou (ex.: para desenhar na bolha).
   */
  metadata: t.optional(
    t.note(
      t.record(t.record(t.any)),
      "pluginId → chave → valor; leia o SEU namespace"
    )
  ),
} as const;

const connectionRef = t.object({ id: t.number, name: t.optional(t.string), number: t.optional(t.string) });

// ─── registro ───────────────────────────────────────────────────────────────

export const ticketsSlots = defineSlotRegistry({
  ACTIONS: {
    name: "ticket-actions",
    description: "Menu ⋮ do cabeçalho do atendimento (itens de menu).",
    context: { ticketId: t.number, contactId: t.number, companyId: t.number },
  },
  HEADER: {
    name: "ticket-header",
    description: "Cabeçalho do atendimento, junto dos botões de ação.",
    context: { ticketId: t.number, contactId: t.number },
  },
  SIDEBAR: {
    name: "ticket-sidebar",
    description:
      "Painel lateral ao lado do chat da conversa aberta; sem conteúdo do plugin não ocupa espaço. Não aparece em telas estreitas.",
    context: { ...ticketOpen },
  },
  LIST_ACTIONS: {
    name: "ticket-list-actions",
    description:
      'Barra ACIMA da lista de tickets, ao lado de "Novo Ticket", "Filas" e "+ Opções". Soma ao que já existe ' +
      "(diferente de new-ticket-button, que substitui). Único ponto da tela que não depende de conversa aberta.",
    context: { companyId: t.number },
  },
  MESSAGE_INPUT: {
    name: "message-input",
    description: "Caixa de digitação: botões ao lado dela e wrap da caixa inteira.",
    context: { ...ticketOpen, draft: t.note(t.string, 'o que já está digitado ("" se nada)') },
  },
  MESSAGE_FORMAT_MENU: {
    name: "message-format-menu",
    description:
      "Menu flutuante ao SELECIONAR texto na caixa (negrito, itálico…). Ação aqui trabalha sobre o trecho selecionado.",
    context: {
      ...ticketOpen,
      draft: t.string,
      selection: t.optional(t.note(t.string, "trecho selecionado na caixa")),
    },
  },
  MESSAGE_ACTIONS: {
    name: "message-actions",
    description: "Menu ⋮ de uma mensagem (itens de menu).",
    context: messageBase,
  },
  MESSAGE_CONTENT: {
    name: "message-content",
    description:
      "Conteúdo da bolha, logo após o texto. Para SUBSTITUIR o texto nativo (emoji como imagem, por exemplo) " +
      "declare também o hook message-content:hide-native e decida pelo channel. " +
      "Neste slot `slot.insertText(texto)` ACRESCENTA ao rascunho da caixa de digitação, sem enviar — " +
      "é como um botão desenhado na bolha oferece a resposta ao atendente, que confere e manda.",
    context: messageOwned,
  },
  MESSAGE_FOOTER: {
    name: "message-footer",
    description: "Rodapé da bolha (junto da hora/ack). Mesmo contrato do conteúdo.",
    context: messageOwned,
  },
  MESSAGE_FILE_CARD: {
    name: "message-file-card",
    description:
      "Card de um arquivo genérico (não-PDF) na bolha. Para SUBSTITUIR o card nativo declare também o hook " +
      "message-file-card:hide-native.",
    context: {
      filename: t.note(t.string, "nome de exibição, com extensão"),
      ext: t.note(t.string, 'minúscula, sem ponto; "" se não houver'),
      mimetype: t.optional(t.string),
      mediaUrl: t.string,
      messageId: t.string,
      wid: t.optional(t.string),
      body: t.string,
      fromMe: t.boolean,
      createdAt: t.note(t.string, "ISO"),
      ticketId: t.number,
      channel: t.string,
      whatsappId: t.optional(t.number),
      isGroup: t.optional(t.boolean),
    },
  },
  MESSAGE_TICKET_SEPARATOR: {
    name: "message-ticket-separator",
    description:
      'Faixa que separa um ticket do outro no histórico ("#Chamado 123 - Fila · Conexão", #4155). Para SUBSTITUIR ' +
      "declare também o hook message-ticket-separator:hide-native.",
    context: {
      ticketId: t.note(t.number, "ticket que começa abaixo da faixa"),
      previousTicketId: t.optional(t.number),
      queue: t.optional(t.object({ id: t.number, name: t.string, color: t.optional(t.string) })),
      connection: t.optional(connectionRef),
      previousConnection: t.optional(connectionRef),
      isConnectionChange: t.note(t.boolean, "a faixa marca troca de CONEXÃO, não só de ticket"),
      channel: t.optional(t.string),
      isGroup: t.optional(t.boolean),
    },
  },
  MESSAGE_REACTIONS: {
    name: "message-reactions",
    description:
      "Faixa de reações abaixo da bolha. Canal cujo emoji é IMAGEM declara também o hook " +
      "message-reactions:hide-native e desenha a figura aqui.",
    context: {
      ...messageOwned,
      reactions: t.note(t.record(t.array(t.string)), 'chave = emoji ou ":nome:"; valor = quem reagiu'),
    },
  },
  NEW: {
    name: "new-ticket",
    description: "Modal de novo atendimento (botões junto ao formulário).",
    context: {
      contactId: t.optional(t.number),
      queueId: t.optional(t.number),
      whatsappId: t.optional(t.number),
      companyId: t.number,
    },
  },
  NEW_BUTTON: {
    name: "new-ticket-button",
    description:
      'O botão "Novo Ticket" acima da lista. Com o hook new-ticket-button:hide-native o plugin SUBSTITUI o nativo.',
    context: { companyId: t.number },
  },
  LIST_ITEM_ACTIONS: {
    name: "ticket-list-item-actions",
    description: "Ações num item da lista de tickets.",
    context: { ticketId: t.number, contactId: t.number },
  },
  LIST_ITEM_BADGE: {
    name: "ticket-list-item-badge",
    description: "Etiqueta curta num item da lista de tickets.",
    context: { ticketId: t.number, contactId: t.number },
  },
});

export const tickets = {
  /** Eventos de pipeline (intercept before/on/after). */
  events: {
    CREATE: "create-ticket",
    ACCEPT: "accept-ticket",
    TRANSFER: "transfer-ticket",
    RESOLVE: "resolve-ticket",
    IGNORE: "ignore-ticket",
    OPEN: "open-ticket",
    SEND_MESSAGE: "send-message",
  },
  /** Slots de render (PluginBridge slot=...) — derivado de `ticketsSlots`. */
  slots: slotNamesOf(ticketsSlots),
  /** Hooks de decisão (registerUIHook). */
  hooks: {
    NEW_BUTTON_HIDE_NATIVE: "new-ticket-button:hide-native",
    /** Esconde o card de arquivo nativo (o plugin renderiza o seu no slot MESSAGE_FILE_CARD). */
    MESSAGE_FILE_CARD_HIDE_NATIVE: "message-file-card:hide-native",
    /** Esconde a faixa separadora nativa (o plugin renderiza a sua no slot MESSAGE_TICKET_SEPARATOR). */
    MESSAGE_TICKET_SEPARATOR_HIDE_NATIVE: "message-ticket-separator:hide-native",
    /**
     * Esconde o texto nativo da bolha (o plugin renderiza o corpo no slot
     * MESSAGE_CONTENT). Decida pelo `channel` do contexto: devolver `true`
     * para tudo apagaria o texto dos outros canais.
     */
    MESSAGE_CONTENT_HIDE_NATIVE: "message-content:hide-native",
    /**
     * Esconde a faixa de reações nativa (o plugin desenha a sua no slot
     * MESSAGE_REACTIONS). Decida pelo `channel` do contexto.
     */
    MESSAGE_REACTIONS_HIDE_NATIVE: "message-reactions:hide-native",
  },
  /** @deprecated Formato antigo (`{ campo: "number?" }`), derivado do registro. Use `ticketsSlots`. */
  slotContext: legacySlotContextOf(ticketsSlots),
  /** O registro: nome + descrição + contrato de cada slot. */
  registry: ticketsSlots,
} as const;

// ─── tipos de context, derivados do registro ────────────────────────────────

export type TicketsSlotContextMap = SlotContextMapOf<typeof ticketsSlots>;

/** Contexto dos slots de mensagem (menu). */
export type MessageActionContext = TicketsSlotContextMap["message-actions"];
/** Contexto dos slots MESSAGE_CONTENT e MESSAGE_FOOTER. */
export type MessageContentContext = TicketsSlotContextMap["message-content"];
/** Contexto do slot MESSAGE_REACTIONS. */
export type MessageReactionsContext = TicketsSlotContextMap["message-reactions"];
/** Contexto do slot MESSAGE_FILE_CARD. */
export type MessageFileCardContext = TicketsSlotContextMap["message-file-card"];
/** Contexto do slot MESSAGE_TICKET_SEPARATOR. */
export type MessageTicketSeparatorContext = TicketsSlotContextMap["message-ticket-separator"];

/**
 * ticket-actions / message-input / etc. — contexto de um ticket aberto.
 * Superconjunto (tudo opcional) dos slots da conversa, aberto para campos
 * extras: é o tipo que os handlers antigos já usavam.
 */
export type TicketSlotContext = Partial<InferShape<typeof ticketsSlots.MESSAGE_FORMAT_MENU.context>> & {
  ticketId?: number | string;
  status?: string;
  [key: string]: any;
};

// ─── contextos dos eventos de pipeline (intercept) ──────────────────────────

/** Ticket como o frontend o conhece (subconjunto estável). */
export interface UITicket {
  id: number;
  uuid?: string;
  status?: string;
  channel?: string;
  isGroup?: boolean;
  contactId?: number;
  queueId?: number;
  userId?: number;
  [k: string]: any;
}

/** create-ticket: payload do ticket + conexão + origem (painel). */
export interface CreateTicketContext extends InterceptContextBase {
  payload: {
    contactId?: number;
    whatsappId?: number;
    userId?: number;
    status?: string;
    [k: string]: any;
  };
  connection?: { id: number; channel: string } | null;
  source?: {
    number?: string;
    contactId?: number | null;
    templateName?: string;
    [k: string]: any;
  };
}

/** accept-ticket: ticket sendo aceito pelo usuário atual. */
export interface AcceptTicketContext extends InterceptContextBase {
  ticket: UITicket;
  userId?: number;
}

/** transfer-ticket: abertura do fluxo de transferência. */
export interface TransferTicketContext extends InterceptContextBase {
  ticket: UITicket;
}

/** resolve-ticket: ticket sendo finalizado/resolvido. */
export interface ResolveTicketContext extends InterceptContextBase {
  ticket: UITicket;
  sendFarewellMessage?: boolean;
  finalizacaoMessage?: string;
}

/** ignore-ticket: ticket sendo fechado sem despedida. */
export interface IgnoreTicketContext extends InterceptContextBase {
  ticket: UITicket;
  userId?: number;
}

/** open-ticket: usuário abriu/selecionou um ticket. */
export interface OpenTicketContext extends InterceptContextBase {
  ticket: UITicket;
}

/** send-message: envio de mensagem de texto. */
export interface SendMessageContext extends InterceptContextBase {
  ticketId: number;
  message: { body: string; fromMe?: boolean; mediaUrl?: string; [k: string]: any };
}
